/**
 * Playoff seeding (matches Playoff picture in SeasonStandings) and single-elimination bracket layout.
 */

import type { PlayoffStructure } from '../model/Season';
import type { TeamStats } from './seasonStandingsCore';
import { compareByTieBreakerPlayoffPool } from './seasonStandingsCore';

export interface PlayoffSeedRow {
    seed: number;
    teamId: string;
    teamName: string;
    coachName: string;
}

export type BracketSide =
    | { kind: 'team'; teamId: string; teamName: string; coachName: string; seed: number }
    | { kind: 'bye' }
    | { kind: 'winnerOf'; matchId: string; label: string };

export interface BracketMatchView {
    id: string;
    roundIndex: number;
    matchIndex: number;
    home: BracketSide;
    away: BracketSide;
}

export function nextPowerOfTwo(n: number): number {
    if (n <= 1) return 1;
    let p = 1;
    while (p < n) p <<= 1;
    return p;
}

/** Same padding the classic bracket uses: how many empty slots are added to reach a power-of-two line (e.g. 6 → 2). */
export function implicitBracketPaddingByes(teamCount: number): number {
    if (teamCount <= 0) return 0;
    return nextPowerOfTwo(teamCount) - teamCount;
}

/** Division winners + wildcards, same rules as SeasonStandings playoff picture (playoff-pool ordering: division points when both teams share a division). */
export function computePlayoffSeeds(
    playoffStructure: PlayoffStructure,
    standingsByDivision: Record<string, TeamStats[]>,
    allTeamsFlat: TeamStats[]
): PlayoffSeedRow[] {
    const perDiv = Math.max(0, playoffStructure.divisionWinnersPerDivision);
    const wildcardCount = Math.max(0, playoffStructure.wildcardCount);
    const divisionWinners: TeamStats[] = [];
    Object.values(standingsByDivision).forEach(divisionTeams => {
        for (let i = 0; i < perDiv && i < divisionTeams.length; i++) {
            divisionWinners.push(divisionTeams[i]);
        }
    });
    divisionWinners.sort(compareByTieBreakerPlayoffPool);
    const winnerIds = new Set(divisionWinners.map(t => t.teamId));
    const nonWinners = allTeamsFlat.filter(t => !winnerIds.has(t.teamId));
    nonWinners.sort(compareByTieBreakerPlayoffPool);
    const wildcards = nonWinners.slice(0, wildcardCount);

    const ordered: TeamStats[] = [...divisionWinners, ...wildcards];
    return ordered.map((t, i) => ({
        seed: i + 1,
        teamId: t.teamId,
        teamName: t.teamName,
        coachName: t.coachName
    }));
}

function toSideFromSeed(row: PlayoffSeedRow): BracketSide {
    return {
        kind: 'team',
        teamId: row.teamId,
        teamName: row.teamName,
        coachName: row.coachName,
        seed: row.seed
    };
}

function shortMatchLabel(roundIndex: number, matchIndex: number): string {
    if (roundIndex === 0) return `R1 M${matchIndex + 1}`;
    if (roundIndex === 1) return `R2 M${matchIndex + 1}`;
    return `R${roundIndex + 1} M${matchIndex + 1}`;
}

/**
 * Classic fixed bracket: line slots 1..S (S = next power of 2), seeds fill from the top,
 * empty slots pair as byes against the top seeds. Each round pairs outer matches until one champion slot.
 */
export function buildSingleEliminationBracket(seeds: PlayoffSeedRow[]): BracketMatchView[][] {
    const n = seeds.length;
    if (n === 0) return [];

    const size = nextPowerOfTwo(n);
    const line: (PlayoffSeedRow | 'bye')[] = [];
    for (let i = 0; i < n; i++) line.push(seeds[i]);
    for (let i = n; i < size; i++) line.push('bye');

    const rounds: BracketMatchView[][] = [];
    let prev: BracketMatchView[] = [];

    for (let i = 0; i < size / 2; i++) {
        const left = line[i];
        const right = line[size - 1 - i];
        const home: BracketSide = left === 'bye' ? { kind: 'bye' } : toSideFromSeed(left);
        const away: BracketSide = right === 'bye' ? { kind: 'bye' } : toSideFromSeed(right);
        prev.push({
            id: `r0-m${i}`,
            roundIndex: 0,
            matchIndex: i,
            home,
            away
        });
    }
    rounds.push(prev);

    let roundIndex = 1;
    while (prev.length > 1) {
        const next: BracketMatchView[] = [];
        const len = prev.length;
        for (let i = 0; i < len / 2; i++) {
            const top = prev[i];
            const bottom = prev[len - 1 - i];
            const labelTop = shortMatchLabel(top.roundIndex, top.matchIndex);
            const labelBot = shortMatchLabel(bottom.roundIndex, bottom.matchIndex);
            next.push({
                id: `r${roundIndex}-m${i}`,
                roundIndex,
                matchIndex: i,
                home: { kind: 'winnerOf', matchId: top.id, label: labelTop },
                away: { kind: 'winnerOf', matchId: bottom.id, label: labelBot }
            });
        }
        rounds.push(next);
        prev = next;
        roundIndex++;
    }

    return rounds;
}

export function roundDisplayName(roundIndex: number, totalRounds: number): string {
    if (totalRounds <= 1) return 'Final';
    const remaining = totalRounds - roundIndex;
    if (remaining === 1) return 'Final';
    if (remaining === 2) return 'Semifinals';
    if (remaining === 3) return 'Quarterfinals';
    return `Round ${roundIndex + 1}`;
}

/** One feeder pair → next-round parent (indices into each round’s match list). */
export interface BracketConnectorSpec {
    roundFrom: number;
    feederA: number;
    feederB: number;
    parentIndex: number;
}

export interface BracketVerticalLayout {
    /** `tops[r][m]` = top offset in px for match m in round r */
    tops: number[][];
    totalHeightPx: number;
    connectors: BracketConnectorSpec[];
}

/**
 * Vertical positions for a classic “tree” bracket: first-round gaps grow with index
 * so nested rounds sit between their feeders without overlapping.
 */
export function computeBracketVerticalLayout(
    rounds: BracketMatchView[][],
    options?: { matchHeightPx?: number; paddingVerticalPx?: number; baseGapPx?: number }
): BracketVerticalLayout {
    const matchHeightPx = options?.matchHeightPx ?? 84;
    const paddingVerticalPx = options?.paddingVerticalPx ?? 28;
    const baseGapPx = options?.baseGapPx ?? 26;

    if (!rounds.length || !rounds[0].length) {
        return { tops: [], totalHeightPx: paddingVerticalPx * 2, connectors: [] };
    }

    const tops: number[][] = [];
    const n0 = rounds[0].length;
    const row0: number[] = [];
    let y = paddingVerticalPx;
    for (let i = 0; i < n0; i++) {
        row0.push(y);
        const gapAfter = i < n0 - 1 ? baseGapPx * Math.pow(2, i) : 0;
        y += matchHeightPx + gapAfter;
    }
    tops.push(row0);

    for (let r = 1; r < rounds.length; r++) {
        const prevTops = tops[r - 1];
        const prevLen = rounds[r - 1].length;
        const row: number[] = [];
        for (let m = 0; m < rounds[r].length; m++) {
            const a = m;
            const b = prevLen - 1 - m;
            const topSpan = Math.min(prevTops[a], prevTops[b]);
            const botSpan = Math.max(prevTops[a] + matchHeightPx, prevTops[b] + matchHeightPx);
            row.push((topSpan + botSpan) / 2 - matchHeightPx / 2);
        }
        tops.push(row);
    }

    let maxBottom = 0;
    for (let r = 0; r < rounds.length; r++) {
        for (let m = 0; m < rounds[r].length; m++) {
            maxBottom = Math.max(maxBottom, tops[r][m] + matchHeightPx);
        }
    }
    const totalHeightPx = maxBottom + paddingVerticalPx;

    const connectors: BracketConnectorSpec[] = [];
    for (let r = 0; r < rounds.length - 1; r++) {
        const len = rounds[r].length;
        for (let i = 0; i < len / 2; i++) {
            connectors.push({
                roundFrom: r,
                feederA: i,
                feederB: len - 1 - i,
                parentIndex: i
            });
        }
    }

    return { tops, totalHeightPx, connectors };
}

/**
 * Vertical layout with each round's matches centered as a column (same baseline height per column).
 * Use when bracket lines do not follow a strict feeder tree between adjacent rounds (e.g. reseed-by-seed format).
 *
 * `connectorMinRoundFrom`: only emit feeder→parent connectors where `roundFrom >=` this value
 * (set to `1` to draw lines only from semifinals onward when quarterfinals use reseed pairing).
 */
export function computeCenteredColumnBracketLayout(
    rounds: BracketMatchView[][],
    options?: {
        matchHeightPx?: number;
        gapPx?: number;
        paddingVerticalPx?: number;
        connectorMinRoundFrom?: number;
    }
): BracketVerticalLayout {
    const matchHeightPx = options?.matchHeightPx ?? 84;
    const gapPx = options?.gapPx ?? 26;
    const paddingVerticalPx = options?.paddingVerticalPx ?? 28;
    const connectorMinRoundFrom = options?.connectorMinRoundFrom ?? 0;

    if (!rounds.length || !rounds[0].length) {
        return { tops: [], totalHeightPx: paddingVerticalPx * 2, connectors: [] };
    }

    let maxColMatches = 0;
    for (const r of rounds) {
        maxColMatches = Math.max(maxColMatches, r.length);
    }
    const colContentH = maxColMatches * matchHeightPx + Math.max(0, maxColMatches - 1) * gapPx;
    const arenaH = colContentH + 2 * paddingVerticalPx;

    const tops: number[][] = [];
    for (let r = 0; r < rounds.length; r++) {
        const n = rounds[r].length;
        const colH = n * matchHeightPx + Math.max(0, n - 1) * gapPx;
        const y0 = paddingVerticalPx + (colContentH - colH) / 2;
        const row: number[] = [];
        for (let i = 0; i < n; i++) {
            row.push(y0 + i * (matchHeightPx + gapPx));
        }
        tops.push(row);
    }

    const connectors: BracketConnectorSpec[] = [];
    for (let r = 0; r < rounds.length - 1; r++) {
        if (r < connectorMinRoundFrom) continue;
        const len = rounds[r].length;
        for (let i = 0; i < len / 2; i++) {
            connectors.push({
                roundFrom: r,
                feederA: i,
                feederB: len - 1 - i,
                parentIndex: i
            });
        }
    }

    return { tops, totalHeightPx: arenaH, connectors };
}

/** All match ids in the subtree rooted at (rootRound, rootMatch) down to round 0. */
export function collectSubtreeMatchIds(
    rounds: BracketMatchView[][],
    rootRound: number,
    rootMatch: number
): Set<string> {
    const ids = new Set<string>();
    const visit = (r: number, m: number) => {
        ids.add(rounds[r][m].id);
        if (r === 0) return;
        const prevLen = rounds[r - 1].length;
        visit(r - 1, m);
        visit(r - 1, prevLen - 1 - m);
    };
    visit(rootRound, rootMatch);
    return ids;
}

export interface MirroredBracketSplit {
    /** Rounds 0..semis-1 on the left side only (each round filtered, in bracket order). */
    leftRounds: BracketMatchView[][];
    /** Same for the right side. */
    rightRounds: BracketMatchView[][];
    finalMatch: BracketMatchView;
    /** True when we have two nonempty wings (typical 4+ team field). */
    isMirrored: boolean;
}

/**
 * Split a single-elimination tree into left / right wings meeting at the final,
 * like conference brackets (AFC vs NFC). Uses the two semifinal subtrees when present.
 */
export function splitBracketForMirroredView(rounds: BracketMatchView[][]): MirroredBracketSplit | null {
    if (rounds.length < 2) return null;

    const finalRound = rounds.length - 1;
    const finalMatch = rounds[finalRound][0];
    const semiRound = rounds.length - 2;

    if (semiRound < 0 || rounds[semiRound].length < 2) {
        return {
            leftRounds: rounds.slice(0, finalRound),
            rightRounds: [],
            finalMatch,
            isMirrored: false
        };
    }

    const leftIds = collectSubtreeMatchIds(rounds, semiRound, 0);
    const rightIds = collectSubtreeMatchIds(rounds, semiRound, 1);

    const buildWing = (idSet: Set<string>): BracketMatchView[][] => {
        const out: BracketMatchView[][] = [];
        for (let r = 0; r < finalRound; r++) {
            const slice = rounds[r].filter(m => idSet.has(m.id));
            if (slice.length > 0) {
                out.push(slice);
            }
        }
        return out;
    };

    const leftRounds = buildWing(leftIds);
    const rightRounds = buildWing(rightIds);
    const isMirrored = leftRounds.length > 0 && rightRounds.length > 0;

    return { leftRounds, rightRounds, finalMatch, isMirrored };
}

/**
 * SVG path `d` strings for a wing laid out left-to-right (lines exit right edge toward center).
 * `xOffset` shifts all coordinates; layout is for `leftRounds` only.
 */
export function buildWingConnectorPathsLeftToRight(
    layout: BracketVerticalLayout,
    xOffset: number,
    colWidth: number,
    gutter: number,
    matchHeight: number
): string[] {
    const { tops, connectors } = layout;
    const paths: string[] = [];
    const cy = (r: number, mi: number) => tops[r][mi] + matchHeight / 2;
    const xRight = (r: number) => xOffset + r * (colWidth + gutter) + colWidth;
    const midX = (r: number) => xRight(r) + gutter / 2;
    const xNext = (r: number) => xOffset + (r + 1) * (colWidth + gutter);

    for (const c of connectors) {
        const { roundFrom, feederA, feederB, parentIndex } = c;
        const yA = cy(roundFrom, feederA);
        const yB = cy(roundFrom, feederB);
        const yP = cy(roundFrom + 1, parentIndex);
        const xr = xRight(roundFrom);
        const xm = midX(roundFrom);
        const xn = xNext(roundFrom);
        paths.push(`M ${xr} ${yA} L ${xm} ${yA}`);
        paths.push(`M ${xr} ${yB} L ${xm} ${yB}`);
        paths.push(`M ${xm} ${Math.min(yA, yB)} L ${xm} ${Math.max(yA, yB)}`);
        paths.push(`M ${xm} ${yP} L ${xn} ${yP}`);
    }
    return paths;
}

/**
 * Connectors for a wing where round index 0 is the **outer** column (toward the arena edge)
 * and winners advance **inward** (decreasing x). `colLeftForRound(r)` is the left edge of column `r`.
 */
export function buildWingConnectorPathsRightWing(
    layout: BracketVerticalLayout,
    colLeftForRound: (roundFrom: number) => number,
    colWidth: number,
    matchHeight: number
): string[] {
    const { tops, connectors } = layout;
    const paths: string[] = [];
    const cy = (r: number, mi: number) => tops[r][mi] + matchHeight / 2;

    for (const c of connectors) {
        const { roundFrom, feederA, feederB, parentIndex } = c;
        const yA = cy(roundFrom, feederA);
        const yB = cy(roundFrom, feederB);
        const yP = cy(roundFrom + 1, parentIndex);

        const outerLeft = colLeftForRound(roundFrom);
        const innerLeft = colLeftForRound(roundFrom + 1);
        const innerRight = innerLeft + colWidth;
        const midX = (innerRight + outerLeft) / 2;

        paths.push(`M ${outerLeft} ${yA} L ${midX} ${yA}`);
        paths.push(`M ${outerLeft} ${yB} L ${midX} ${yB}`);
        paths.push(`M ${midX} ${Math.min(yA, yB)} L ${midX} ${Math.max(yA, yB)}`);
        paths.push(`M ${midX} ${yP} L ${innerLeft} ${yP}`);
    }
    return paths;
}

/** Bridge lines from inner edge of each wing to the final box (horizontal). */
export function buildBridgePathsToFinal(params: {
    leftInnerRightX: number;
    leftFinalistY: number;
    finalLeftX: number;
    finalRightX: number;
    rightFinalistY: number;
    rightInnerLeftX: number;
}): string[] {
    const { leftInnerRightX, leftFinalistY, finalLeftX, finalRightX, rightFinalistY, rightInnerLeftX } = params;
    const paths: string[] = [];
    paths.push(`M ${leftInnerRightX} ${leftFinalistY} L ${finalLeftX} ${leftFinalistY}`);
    paths.push(`M ${finalRightX} ${rightFinalistY} L ${rightInnerLeftX} ${rightFinalistY}`);
    return paths;
}
